/**
 * VÉRAULT Member Suite — Client · Itinerary (Wave 26.23 · Phase 3a).
 * ───────────────────────────────────────────────────────────────────
 * Editorial day-chapter system. Each day opens like a quietly-designed
 * travel-magazine spread:
 *
 *   ▸ Full-bleed day hero (image · arrival/departure pill · atmospheric overlay)
 *   ▸ Day intro (date eyebrow · serif title · italic subheading · weather chip · curated notes)
 *   ▸ Activity timeline — continuous vertical flow with numbered dots,
 *     soft block dividers (Morning · Afternoon · Evening), and
 *     activity cards that expand smoothly from quiet single-line to
 *     immersive full-image detail.
 *   ▸ Secret-day cards preserved (Wave 26.21 Phase 2b unlock countdowns
 *     and poetic hints still surface here).
 *
 * Phase 3a discipline:
 *   · Foundation only — no map redesign yet (Phase 3b)
 *   · No cost transparency layer yet (Phase 3c)
 *   · No approval workflow yet (Phase 3d)
 *   · Preserve all existing data contracts (engine slot keys, items[],
 *     secret-day visibility states).
 */
import React from "react";
import { useParams, Link, useOutletContext, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ChevronLeft, ChevronRight, ChevronDown, MapPin, CalendarDays, Plane, Car, Train, Hotel,
  Utensils, Camera, Footprints, Sparkles, Lock, Sun, Cloud, CloudRain, Snowflake, Heart,
  ExternalLink, Phone, Globe, ArrowUpRight, Check, RotateCcw,
} from "lucide-react";
import {
  Heading,
  EditorialRule,
  PortalButton,
  Empty,
  Skeleton,
  api,
  fmt,
  motion as portalMotion,
} from "../../index";
import { usePreviewBase } from "../../contexts/PreviewContext";
import { useDestinationVeil } from "../../../hooks/useDestinationVeil";
import { RouteMap, collectGeocodedPoints } from "./experience/editorial/RouteMap";
import { TripDestinationRibbon } from "./experience/editorial/TripDestinationRibbon";
import { CostStatusChip, CostStatusLine } from "./experience/editorial/CostStatusChip";
import { resolveTransportDisplay } from "../advisor/journey/transportation";
import { formatTime12h } from "../../lib/timeFormat";
import { InvestmentBand } from "./experience/editorial/InvestmentBand";
import { ApprovalBlock } from "./experience/editorial/ApprovalBlock";
import { LiveDayBanner } from "./experience/editorial/LiveDayBanner";
import { useArrivalFocus } from "../../../hooks/useArrivalFocus";

const { fadeInUp, stagger } = portalMotion;
const EASE_CALM = [0.22, 0.61, 0.36, 1];

// ═══════════════════════════════════════════════════════════════════
// Item category heuristics — pick a calm Lucide glyph per activity.
// Mirrors the smart 4K image pool router from Wave 26.18i so the icon
// and the photo agree.
// ═══════════════════════════════════════════════════════════════════
function pickItemIcon(item, blockKey) {
  const txt = `${item?.title || item?.name || item?.activity || ""} ${item?.type || ""}`.toLowerCase();
  if (txt.includes("flight") || txt.includes("airline") || txt.includes("airport")) return Plane;
  if (txt.includes("transfer") || txt.includes("car") || txt.includes("driver")) return Car;
  if (txt.includes("train") || txt.includes("rail")) return Train;
  if (txt.includes("hotel") || txt.includes("stay") || txt.includes("check-in")) return Hotel;
  if (txt.includes("museum") || txt.includes("gallery")) return Camera;
  if (txt.includes("walk") || txt.includes("stroll") || txt.includes("hike")) return Footprints;
  if (item?.category === "meal" || blockKey === "morning" && txt.includes("breakfast")) return Utensils;
  if (txt.includes("breakfast") || txt.includes("lunch") || txt.includes("dinner") || txt.includes("café") || txt.includes("cafe") || txt.includes("restaurant")) return Utensils;
  return Sparkles;
}

function pickItemCategoryLabel(item, blockKey) {
  const txt = `${item?.title || item?.name || item?.activity || ""}`.toLowerCase();
  if (txt.includes("breakfast")) return "Breakfast";
  if (txt.includes("brunch")) return "Brunch";
  if (txt.includes("lunch")) return "Lunch";
  if (txt.includes("dinner")) return "Dinner";
  if (txt.includes("museum") || txt.includes("gallery")) return "Cultural";
  if (txt.includes("hike") || txt.includes("walk") || txt.includes("stroll") || txt.includes("garden") || txt.includes("park")) return "Outdoors";
  if (txt.includes("spa") || txt.includes("wellness")) return "Wellness";
  if (txt.includes("shop") || txt.includes("boutique") || txt.includes("market")) return "Shopping";
  if (txt.includes("flight")) return "Flight";
  if (txt.includes("transfer") || txt.includes("car") || txt.includes("train")) return "Transfer";
  if (txt.includes("hotel") || txt.includes("stay") || txt.includes("check-in")) return "Stay";
  if (blockKey === "morning") return "Morning";
  if (blockKey === "afternoon") return "Afternoon";
  if (blockKey === "evening") return "Evening";
  if (blockKey === "late_night") return "Late Night";
  return "Experience";
}

// Wave 27.1 · Phase 4 — Smart Essentials anchor router. Routes any
// itinerary item to the right Essentials section via hash, so a member
// can pivot from the chronological flow into the operational sanctuary
// without losing context.
function pickEssentialsTarget(item, blockKey) {
  const txt = `${item?.title || item?.name || item?.activity || ""} ${item?.type || item?.item_type || ""}`.toLowerCase();
  if (txt.includes("flight") || txt.includes("transfer") || txt.includes("train") || txt.includes("airline") || txt.includes("airport") || txt.includes("rail") || txt.includes("ferry")) {
    return { anchor: "transportation", label: "View in Essentials · Transportation" };
  }
  if (txt.includes("hotel") || txt.includes("stay") || txt.includes("check-in") || txt.includes("resort") || txt.includes("villa") || txt.includes("ryokan") || txt.includes("cruise")) {
    return { anchor: "stays", label: "View stay in Essentials" };
  }
  // Default: route to balances (most line items have a cost association).
  return { anchor: "balances", label: "View in Essentials · Balances" };
}

// ═══════════════════════════════════════════════════════════════════
// WeatherChip — quiet inline forecast for the day's destination.
// Reads from /api/weather/{destination}; module-cached so a 7-day
// itinerary makes at most one network call per unique destination.
// Defaults to °F (Wave 26.19d). Silent on failure — never blocks render.
// ═══════════════════════════════════════════════════════════════════
const _weatherCache = new Map();

function WeatherIcon({ code }) {
  // Open-Meteo weather codes — collapsed to a tiny set of icons.
  if (code == null) return null;
  if (code === 0 || code === 1) return <Sun className="size-3.5" strokeWidth={1.5} />;
  if (code === 2 || code === 3) return <Cloud className="size-3.5" strokeWidth={1.5} />;
  if ((code >= 51 && code <= 67) || (code >= 80 && code <= 82)) return <CloudRain className="size-3.5" strokeWidth={1.5} />;
  if ((code >= 71 && code <= 77) || (code >= 85 && code <= 86)) return <Snowflake className="size-3.5" strokeWidth={1.5} />;
  return <Cloud className="size-3.5" strokeWidth={1.5} />;
}

function WeatherChip({ destination, date }) {
  const [w, setW] = React.useState(_weatherCache.get(destination) || null);
  React.useEffect(() => {
    if (!destination || _weatherCache.has(destination)) return undefined;
    let alive = true;
    api.get(`/weather/${encodeURIComponent(destination.split(/[·,]/)[0].trim())}`)
      .then(({ data }) => {
        if (data?.error) return;
        _weatherCache.set(destination, data);
        if (alive) setW(data);
      })
      .catch(() => { /* silent */ });
    return () => { alive = false; };
  }, [destination]);
  if (!w || w.error) return null;
  const unit = (typeof window !== "undefined" && window.localStorage?.getItem("verault_temp_unit")) || "fahrenheit";
  // Wave 12.aa — weather endpoint returns nested `current.*` strings
  // ("18°C" / "64°F") and `forecast[]` with daily high/low. Parse them
  // gracefully so the chip stays accurate.
  const current = w.current || {};
  const tempStr = unit === "celsius" ? (current.temperature || w.temperature) : (current.temperature_f || w.temperature_f || w.temperature);
  const cond = (current.condition || w.current_conditions || w.condition || "").toString();
  // Find matching forecast row for the requested date (falls back to first).
  let hi = null;
  let lo = null;
  if (Array.isArray(w.forecast) && w.forecast.length) {
    const target = (date || "").slice(0, 10);
    const match = w.forecast.find((f) => (f.date || "").slice(0, 10) === target) || w.forecast[0];
    if (match) {
      // forecast highs/lows are Celsius numbers — convert for °F if needed.
      const toF = (c) => (c == null ? null : Math.round((c * 9) / 5 + 32));
      hi = unit === "celsius" ? (match.high != null ? Math.round(match.high) : null) : toF(match.high);
      lo = unit === "celsius" ? (match.low != null ? Math.round(match.low) : null) : toF(match.low);
    }
  }
  const sym = unit === "celsius" ? "°C" : "°F";
  return (
    <span
      data-testid="day-weather-chip"
      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/85 backdrop-blur-sm border border-[rgba(0,0,0,0.05)] font-mono text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--foreground-secondary))]"
    >
      <WeatherIcon code={w.weather_code} />
      {tempStr ? <span>{String(tempStr).replace(/[°CF]+/g, "")}{sym}</span> : null}
      {hi != null && lo != null && (
        <span className="opacity-70" title="High / low">· {hi}{sym} / {lo}{sym}</span>
      )}
      {cond ? <span className="opacity-70 normal-case tracking-normal italic">· {cond.toLowerCase()}</span> : null}
    </span>
  );
}

// ═══════════════════════════════════════════════════════════════════
// Wave 11w · Preference Intelligence — Reaction block
// ───────────────────────────────────────────────────────────────────
// Per-activity rating ("loved" / "liked" / "meh") + a free-text review
// note. Persists to /api/client-portal/trips/{trip}/items/{item}/reaction
// and feeds the client's learned preference profile.
// ═══════════════════════════════════════════════════════════════════
const RATING_PALETTE = {
  loved: { label: "Loved it", color: "hsl(0,55%,50%)", icon: "♥" },
  liked: { label: "Liked it", color: "hsl(var(--accent-gold))", icon: "✓" },
  meh: { label: "Not for me", color: "hsl(var(--foreground-muted))", icon: "○" },
};

function ReactionBlock({ item, dayNumber, blockKey, tripId }) {
  const [rating, setRating] = React.useState(null);
  const [note, setNote] = React.useState("");
  const [saving, setSaving] = React.useState(false);
  const [persistedNote, setPersistedNote] = React.useState("");
  const [hydrated, setHydrated] = React.useState(false);

  // Hydrate existing reaction on mount.
  React.useEffect(() => {
    if (!tripId || !item?.id) return;
    let alive = true;
    api.get(`/client-portal/trips/${tripId}/reactions`).then(({ data }) => {
      if (!alive) return;
      const existing = (data?.reactions || []).find((r) => r.item_id === item.id);
      if (existing) {
        setRating(existing.rating || null);
        setNote(existing.note || "");
        setPersistedNote(existing.note || "");
      }
      setHydrated(true);
    }).catch(() => alive && setHydrated(true));
    return () => { alive = false; };
  }, [tripId, item?.id]);

  const persist = async (nextRating, nextNote) => {
    setSaving(true);
    try {
      await api.put(`/client-portal/trips/${tripId}/items/${item.id}/reaction`, {
        day_number: dayNumber,
        block: blockKey,
        rating: nextRating,
        note: nextNote || "",
      });
      setPersistedNote(nextNote || "");
    } catch (e) {
      // calm failure — keep state
    } finally {
      setSaving(false);
    }
  };

  const onRate = (r) => {
    const next = rating === r ? null : r;
    setRating(next);
    persist(next, note);
  };

  const onSaveNote = () => {
    persist(rating, note);
  };

  const hasUnsavedNote = note !== persistedNote;

  return (
    <div
      className="mt-2 pt-4 border-t border-[rgba(0,0,0,0.06)] space-y-3"
      data-testid={`reaction-block-${blockKey}-${dayNumber}-${item.id}`}
    >
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <p className="font-mono text-[9.5px] uppercase tracking-[0.22em] text-[hsl(var(--foreground-muted))]">
          A note for your advisor
        </p>
        <div className="flex items-center gap-1">
          {Object.entries(RATING_PALETTE).map(([key, p]) => (
            <button
              key={key}
              type="button"
              onClick={() => onRate(key)}
              disabled={!hydrated || saving}
              className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full font-mono text-[10px] uppercase tracking-[0.16em] transition-all border ${
                rating === key
                  ? "text-white shadow-sm"
                  : "text-[hsl(var(--foreground-muted))] bg-white hover:bg-[hsl(var(--background-paper))] border-[rgba(0,0,0,0.08)]"
              }`}
              style={rating === key ? { backgroundColor: p.color, borderColor: p.color } : undefined}
              aria-pressed={rating === key}
              data-testid={`reaction-rate-${key}-${item.id}`}
            >
              <span aria-hidden style={{ fontSize: "11px" }}>{p.icon}</span>
              {p.label}
            </button>
          ))}
        </div>
      </div>
      <div>
        <textarea
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="A quick line to remember — what worked, what didn't, anything you'd love more of next time…"
          rows={2}
          className="w-full px-3 py-2 rounded-md border border-[rgba(0,0,0,0.08)] bg-white font-accent italic text-[13px] leading-relaxed resize-y focus:outline-none focus:border-[hsl(var(--accent-gold))]"
          data-testid={`reaction-note-${item.id}`}
        />
        <div className="mt-1.5 flex items-center justify-between gap-3">
          <p className="font-accent italic text-[11px] text-[hsl(var(--foreground-muted))]">
            {persistedNote && !hasUnsavedNote
              ? "Note saved — your advisor will see this."
              : "Helps shape your next trip's recommendations."}
          </p>
          {hasUnsavedNote && (
            <button
              type="button"
              onClick={onSaveNote}
              disabled={saving}
              className="font-mono text-[10px] uppercase tracking-[0.2em] text-[hsl(var(--accent-gold))] hover:underline disabled:opacity-50"
              data-testid={`reaction-save-${item.id}`}
            >
              {saving ? "Saving…" : "Save note"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// Wave 31 · OptionChoiceCard — one card inside an OptionBlockCard.
// Card is the primary pick affordance; an inline "More details ↓"
// expander surfaces the full itinerary-block detail panel for that
// option (notes, important details, reservation, website, phone,
// dress code, cost). Clicking inside the expanded panel does NOT
// trigger the pick — only the card surface does.
// ═══════════════════════════════════════════════════════════════════
function OptionChoiceCard({ option: opt, isSelected, isDimmed, disabled, onPick, testIdPrefix }) {
  const [open, setOpen] = React.useState(false);

  const importantDetails = React.useMemo(() => {
    return (opt.important_details || "")
      .split(/\r?\n/).map((s) => s.trim()).filter(Boolean);
  }, [opt.important_details]);
  const reservationDetails = React.useMemo(() => {
    return (opt.reservation_details || "")
      .split(/\r?\n/).map((s) => s.trim()).filter(Boolean);
  }, [opt.reservation_details]);

  const mapsHref = React.useMemo(() => {
    if (!opt.address && !opt.latitude) return null;
    const ua = (typeof navigator !== "undefined" && navigator.userAgent) || "";
    const isApple = /iPhone|iPad|iPod|Macintosh/i.test(ua);
    if (opt.latitude && opt.longitude) {
      return isApple
        ? `https://maps.apple.com/?ll=${opt.latitude},${opt.longitude}&q=${encodeURIComponent(opt.name || "")}`
        : `https://www.google.com/maps?q=${opt.latitude},${opt.longitude}`;
    }
    const q = encodeURIComponent(opt.address);
    return isApple
      ? `https://maps.apple.com/?q=${q}`
      : `https://www.google.com/maps/search/?api=1&query=${q}`;
  }, [opt.latitude, opt.longitude, opt.address, opt.name]);

  const showCost = opt.cost_visible_to_client && (opt.cost_amount || opt.cost_status);
  const hasMore = !!(
    opt.notes || importantDetails.length || reservationDetails.length ||
    opt.dress_code || opt.website || opt.booking_link || opt.phone || showCost || opt.description
  );

  return (
    <div
      data-testid={`${testIdPrefix}-${opt.id}`}
      className={`relative rounded-md overflow-hidden border transition-all ${
        isSelected
          ? "border-[hsl(var(--accent-gold))] ring-2 ring-[hsl(var(--accent-gold))]/30 shadow-[0_8px_24px_-12px_rgba(212,175,55,0.45)]"
          : "border-[rgba(0,0,0,0.06)] hover:border-[hsl(var(--accent-gold))]/40"
      } ${isDimmed ? "opacity-55 hover:opacity-90" : ""}`}
    >
      <button
        type="button"
        onClick={onPick}
        disabled={disabled}
        aria-pressed={isSelected}
        data-testid={`${testIdPrefix}-${opt.id}-pick`}
        className="w-full text-left group"
      >
        <div className="relative aspect-[4/3] bg-[hsl(var(--background-stone))] overflow-hidden">
          {opt.image_url ? (
            <img
              src={opt.image_url}
              alt={opt.name}
              loading="lazy"
              className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.03]"
            />
          ) : (
            <div className="absolute inset-0 bg-gradient-to-br from-[hsl(var(--background-paper))] to-[hsl(var(--background-stone))]" />
          )}
          {isSelected && (
            <div className="absolute top-2 right-2 inline-flex items-center gap-1 px-2 py-1 rounded-full bg-[hsl(var(--accent-gold))] text-[hsl(var(--foreground))] font-mono text-[9.5px] uppercase tracking-[0.2em]">
              <Check className="size-3" strokeWidth={2} /> Your pick
            </div>
          )}
        </div>
        <div className="p-3 sm:p-4 space-y-1.5">
          <div className="flex items-start justify-between gap-2">
            <p className="font-serif text-[15px] sm:text-[17px] leading-tight">
              {opt.name}
            </p>
            {showCost && (
              <CostStatusChip status={opt.cost_status} />
            )}
          </div>
          {(opt.time || opt.address) && (
            <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--foreground-muted))]">
              {[opt.time, opt.address].filter(Boolean).join(" · ")}
            </p>
          )}
          {opt.description && (
            <p className="font-accent italic text-[13px] text-[hsl(var(--foreground-secondary))] line-clamp-2">
              {opt.description}
            </p>
          )}
        </div>
      </button>

      {hasMore && (
        <div className="border-t border-[rgba(0,0,0,0.05)]">
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            data-testid={`${testIdPrefix}-${opt.id}-more`}
            className="w-full px-3 sm:px-4 py-2 flex items-center justify-between gap-2 font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--foreground-muted))] hover:text-[hsl(var(--foreground))] transition-colors"
            aria-expanded={open}
          >
            <span>{open ? "Hide details" : "More details"}</span>
            <ChevronDown className={`size-3 transition-transform duration-500 ${open ? "rotate-180" : ""}`} strokeWidth={1.5} />
          </button>
          <AnimatePresence initial={false}>
            {open && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.45, ease: EASE_CALM }}
                className="overflow-hidden"
                data-testid={`${testIdPrefix}-${opt.id}-details`}
              >
                <div className="px-3 sm:px-4 pb-4 pt-1 space-y-3">
                  {showCost && (
                    <CostStatusLine
                      amount={opt.cost_amount}
                      currency={opt.cost_currency}
                      status={opt.cost_status}
                      notes={opt.cost_notes}
                    />
                  )}
                  {opt.notes && (
                    <p className="font-accent italic text-[13px] text-[hsl(var(--foreground-secondary))] leading-relaxed">
                      {opt.notes}
                    </p>
                  )}
                  {importantDetails.length > 0 && (
                    <div className="space-y-1">
                      <p className="font-mono text-[9.5px] uppercase tracking-[0.22em] text-[hsl(var(--foreground-muted))]">
                        Good to know
                      </p>
                      <ul className="space-y-1">
                        {importantDetails.map((d, di) => (
                          <li key={di} className="font-serif text-[13px] text-[hsl(var(--foreground-secondary))] leading-snug flex gap-2">
                            <span aria-hidden className="mt-1.5 inline-block size-1 rounded-full bg-[hsl(var(--accent-gold))]/70 shrink-0" />
                            <span>{d}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {reservationDetails.length > 0 && (
                    <div className="space-y-1">
                      <p className="font-mono text-[9.5px] uppercase tracking-[0.22em] text-[hsl(var(--foreground-muted))]">
                        Reservation
                      </p>
                      <ul className="space-y-1">
                        {reservationDetails.map((d, di) => (
                          <li key={di} className="font-serif text-[13px] text-[hsl(var(--foreground-secondary))] leading-snug">
                            {d}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {opt.dress_code && (
                    <p className="font-accent italic text-[12.5px] text-[hsl(var(--foreground-muted))]">
                      <span className="font-mono not-italic text-[9.5px] uppercase tracking-[0.22em] mr-1.5">What to wear ·</span>
                      {opt.dress_code}
                    </p>
                  )}
                  {(opt.website || opt.booking_link || opt.phone || mapsHref) && (
                    <div className="flex flex-wrap gap-2 pt-1">
                      {mapsHref && (
                        <a
                          href={mapsHref}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[hsl(var(--background-stone))]/60 border border-[rgba(0,0,0,0.06)] hover:border-[hsl(var(--accent-gold))]/40 transition-colors font-mono text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--foreground-muted))] hover:text-[hsl(var(--foreground))]"
                          data-testid={`${testIdPrefix}-${opt.id}-map`}
                        >
                          <MapPin className="size-3" strokeWidth={1.5} /> Map
                        </a>
                      )}
                      {opt.website && (
                        <a
                          href={opt.website}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[hsl(var(--background-stone))]/60 border border-[rgba(0,0,0,0.06)] hover:border-[hsl(var(--accent-gold))]/40 transition-colors font-mono text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--foreground-muted))] hover:text-[hsl(var(--foreground))]"
                          data-testid={`${testIdPrefix}-${opt.id}-website`}
                        >
                          <Globe className="size-3" strokeWidth={1.5} /> Website
                        </a>
                      )}
                      {opt.booking_link && (
                        <a
                          href={opt.booking_link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[hsl(var(--background-stone))]/60 border border-[rgba(0,0,0,0.06)] hover:border-[hsl(var(--accent-gold))]/40 transition-colors font-mono text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--foreground-muted))] hover:text-[hsl(var(--foreground))]"
                          data-testid={`${testIdPrefix}-${opt.id}-booking`}
                        >
                          <ExternalLink className="size-3" strokeWidth={1.5} /> Book
                        </a>
                      )}
                      {opt.phone && (
                        <a
                          href={`tel:${opt.phone}`}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[hsl(var(--background-stone))]/60 border border-[rgba(0,0,0,0.06)] hover:border-[hsl(var(--accent-gold))]/40 transition-colors font-mono text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--foreground-muted))] hover:text-[hsl(var(--foreground))]"
                          data-testid={`${testIdPrefix}-${opt.id}-phone`}
                        >
                          <Phone className="size-3" strokeWidth={1.5} /> Call
                        </a>
                      )}
                    </div>
                  )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      )}
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════
// Wave 31 · OptionBlockCard — "Choose your evening" client picker.
// Shows 2–3 image-led option cards side-by-side. Tap to select.
// First tap wins; the picker can be re-opened via "Change selection".
// ═══════════════════════════════════════════════════════════════════
function OptionBlockCard({ item, idx, blockKey, dayNumber, tripId }) {
  const [selection, setSelection] = React.useState(item.client_selection || null);
  const [busy, setBusy] = React.useState(false);
  const options = Array.isArray(item.options) ? item.options : [];
  const selectedId = selection?.option_id || null;
  const selectedOption = selectedId
    ? options.find((o) => o.id === selectedId)
    : null;
  const number = String(idx + 1).padStart(2, "0");

  const pick = async (optionId) => {
    setBusy(true);
    try {
      const { data } = await api.post(
        `/client-portal/trips/${tripId}/option-blocks/${item.id}/select`,
        { option_id: optionId }
      );
      setSelection(data.client_selection);
    } catch (e) {
      // Calm fail — leave previous state in place; surface a quiet alert.
      // eslint-disable-next-line no-console
      console.error("[OptionBlock] select failed", e);
    } finally {
      setBusy(false);
    }
  };

  const clear = async () => {
    setBusy(true);
    try {
      await api.delete(`/client-portal/trips/${tripId}/option-blocks/${item.id}/select`);
      setSelection(null);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error("[OptionBlock] clear failed", e);
    } finally {
      setBusy(false);
    }
  };

  return (
    <motion.div
      layout
      transition={{ duration: 0.5, ease: EASE_CALM }}
      className="relative pl-9 sm:pl-12"
      id={`option-block-${blockKey}-${idx}`}
      data-testid={`option-block-${blockKey}-${idx}`}
    >
      <span
        aria-hidden
        className="absolute left-0 top-1 inline-flex items-center justify-center size-7 sm:size-9 rounded-full font-mono text-[10px] tracking-[0.05em] bg-[hsl(var(--background-paper))] border border-[rgba(0,0,0,0.08)] text-[hsl(var(--foreground-muted))]"
      >
        {number}
      </span>

      <div className="space-y-3">
        <div className="flex items-baseline justify-between gap-3 flex-wrap">
          <h4 className="font-serif text-[20px] sm:text-[24px] leading-tight text-[hsl(var(--foreground))]">
            {item.name || "Choose your evening"}
          </h4>
          {selection && (
            <p
              className="font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--accent-gold))]"
              data-testid={`option-block-selected-meta-${blockKey}-${idx}`}
            >
              You chose · {selectedOption?.name || "—"}
            </p>
          )}
        </div>

        {item.description && (
          <p className="font-accent italic text-[14px] text-[hsl(var(--foreground-muted))] max-w-2xl">
            {item.description}
          </p>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
          {options.map((opt) => (
            <OptionChoiceCard
              key={opt.id}
              option={opt}
              isSelected={selectedId === opt.id}
              isDimmed={!!selectedId && selectedId !== opt.id}
              disabled={busy}
              onPick={() => !busy && selectedId !== opt.id && pick(opt.id)}
              testIdPrefix={`option-block-choice-${blockKey}-${idx}`}
            />
          ))}
        </div>

        {selection ? (
          <p className="font-accent italic text-[12.5px] text-[hsl(var(--foreground-muted))] pt-1">
            Selected by {selection.selected_by_name || "you"}.
            {" "}
            <button
              type="button"
              onClick={clear}
              disabled={busy}
              data-testid={`option-block-clear-${blockKey}-${idx}`}
              className="inline-flex items-center gap-1 underline decoration-dotted underline-offset-2 hover:text-[hsl(var(--foreground))] transition-colors"
            >
              <RotateCcw className="size-3" strokeWidth={1.5} /> Change selection
            </button>
          </p>
        ) : (
          <p className="font-accent italic text-[12.5px] text-[hsl(var(--foreground-muted))] pt-1">
            Tap a card to choose — you can change your mind later.
          </p>
        )}
      </div>
    </motion.div>
  );
}


// ═══════════════════════════════════════════════════════════════════
// ActivityCard — collapsed / expanded with smooth motion.
// Collapsed: numbered-dot · time · title · category eyebrow chip.
// Expanded: 4K hero image, location, notes, advisor commentary.
// Wave 26.24 · Phase 3b — participates in cross-highlight via
// `focusedKey` + `onFocusChange` (paired with RouteMap).
// ═══════════════════════════════════════════════════════════════════
function ActivityCard({ item, idx, blockKey, dayNumber, focusKey, focusedKey, onFocusChange, tripId, savedSet, onToggleSave }) {
  const [open, setOpen] = React.useState(false);
  const Icon = pickItemIcon(item, blockKey);
  const category = pickItemCategoryLabel(item, blockKey);
  const title = item.title || item.name || item.activity || "Reservation";
  const time = formatTime12h(item.time || item.start_time);
  const endTime = formatTime12h(item.end_time);
  const location = item.location || item.address;
  const notes = item.notes || item.advisor_notes;
  // Wave 12.z — Content layer restructure (Phase 2).  Both fields are
  // simple newline-separated strings; we split into trimmed bullets
  // and skip empty lines so a stray blank line in the editor doesn't
  // leak.
  const importantDetails = React.useMemo(() => {
    return (item.important_details || "")
      .split(/\r?\n/)
      .map((s) => s.trim())
      .filter(Boolean);
  }, [item.important_details]);
  const reservationDetails = React.useMemo(() => {
    return (item.reservation_details || "")
      .split(/\r?\n/)
      .map((s) => s.trim())
      .filter(Boolean);
  }, [item.reservation_details]);
  const [reservationOpen, setReservationOpen] = React.useState(false);
  const image = item.image_url;
  const confirmation = item.confirmation_number || item.confirmation;
  const number = String(idx + 1).padStart(2, "0");
  const isFocused = focusedKey && focusedKey === focusKey;
  // Wave 11o — option blocks (extras) get the heart + "advisor recommended" badge.
  const isOption = !!item._is_option;
  const advisorRecommended = !!item._advisor_recommended;
  const isSaved = isOption && savedSet && savedSet.has(item.id);
  // Wave 26.25 · Phase 3c — cost data is present only when the
  // advisor has flagged this item visible to the client (the backend
  // strips cost_* fields otherwise).
  const costStatus = item.cost_status;
  const costAmount = item.cost_amount;
  const costCurrency = item.cost_currency || "USD";
  const costNotes = item.cost_notes;
  // Wave 11v — comprehensive expanded view fields.
  const website = item.website || item.url;
  const bookingLink = item.booking_link || item.reservation_link || item.source_url;
  const phone = item.phone || item.phone_number;
  const priceEstimate = item.price_estimate;
  const reservationStatus = item.reservation_status;
  // Wave 12.n — Smart Maps href. Uses Apple Maps on iOS/macOS (so the
  // address opens natively in Maps.app), Google Maps everywhere else.
  // Falls back to lat/lng when present, otherwise the address text.
  const mapsHref = React.useMemo(() => {
    const ua = (typeof navigator !== "undefined" && navigator.userAgent) || "";
    const platform = (typeof navigator !== "undefined" && navigator.platform) || "";
    const isApple = /iPhone|iPad|iPod|Macintosh/i.test(ua) || /Mac|iPhone|iPad|iPod/i.test(platform);
    if (item.latitude && item.longitude) {
      return isApple
        ? `https://maps.apple.com/?ll=${item.latitude},${item.longitude}&q=${encodeURIComponent(item.name || "")}`
        : `https://www.google.com/maps?q=${item.latitude},${item.longitude}`;
    }
    if (location) {
      const q = encodeURIComponent(location);
      return isApple
        ? `https://maps.apple.com/?q=${q}`
        : `https://www.google.com/maps/search/?api=1&query=${q}`;
    }
    return null;
  }, [item.latitude, item.longitude, item.name, location]);

  return (
    <motion.div
      layout
      transition={{ duration: 0.5, ease: EASE_CALM }}
      className="relative pl-9 sm:pl-12"
      id={`activity-${blockKey}-${idx}`}
      data-testid={`activity-${blockKey}-${idx}`}
      data-activity-open={open ? "true" : "false"}
      data-focus-key={focusKey}
      data-focused={isFocused ? "true" : "false"}
      onMouseEnter={() => onFocusChange && onFocusChange(focusKey)}
      onMouseLeave={() => onFocusChange && onFocusChange(null)}
    >
      {/* Numbered dot on the timeline — gold-fills on focus */}
      <span
        aria-hidden
        className={`absolute left-0 top-1 inline-flex items-center justify-center size-7 sm:size-9 rounded-full font-mono text-[10px] tracking-[0.05em] transition-colors ${
          isFocused
            ? "bg-[hsl(var(--accent-gold))] border border-[hsl(var(--accent-gold))] text-[hsl(var(--foreground))]"
            : "bg-[hsl(var(--background-paper))] border border-[rgba(0,0,0,0.08)] text-[hsl(var(--foreground-muted))]"
        }`}
      >
        {number}
      </span>

      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full text-left group"
        aria-expanded={open}
        data-testid={`activity-toggle-${blockKey}-${idx}`}
      >
        <div className="flex items-start justify-between gap-3">
          {/* Wave 11v — Tiny thumbnail in the collapsed state. Slides
              up into the full hero when the card expands. Falls back
              to a calm Icon tile when no image is present. */}
          {!open && (
            <div
              className="hidden sm:block shrink-0 w-14 h-14 rounded-md overflow-hidden bg-[hsl(var(--background-paper))] border border-[rgba(0,0,0,0.06)]"
              aria-hidden
            >
              {image ? (
                <img
                  src={image}
                  alt=""
                  loading="lazy"
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-[hsl(var(--foreground-muted))]">
                  <Icon className="size-5" strokeWidth={1.25} />
                </div>
              )}
            </div>
          )}

          <div className="flex-1 min-w-0">
            <div className="flex items-baseline gap-2.5 flex-wrap">
              {time && (
                <span className="font-mono text-[11px] tracking-[0.18em] text-[hsl(var(--foreground-muted))] tabular-nums shrink-0">
                  {time}
                </span>
              )}
              <span className="font-serif text-[17px] sm:text-[18px] leading-snug tracking-tight text-[hsl(var(--foreground))] group-hover:text-[hsl(var(--accent-gold))] transition-colors">
                {title}
              </span>
              {/* Wave 26.25 · Phase 3c — calm cost chip beside the title.
                  Renders only when the advisor has shared cost data. */}
              {costStatus && (
                <CostStatusChip
                  status={costStatus}
                  amount={costAmount}
                  currency={costCurrency}
                  showAmount={!open}
                />
              )}
            </div>
            <div className="mt-1 flex items-center gap-2 text-[hsl(var(--foreground-muted))] flex-wrap">
              <Icon className="size-3 shrink-0" strokeWidth={1.5} />
              <span className="font-mono text-[10px] uppercase tracking-[0.22em]">{category}</span>
              {location && (
                <>
                  <span aria-hidden className="opacity-40">·</span>
                  <span className="font-accent italic text-[12.5px] truncate">{location.split(",")[0]}</span>
                </>
              )}
              {/* Wave 12.aa · client-facing status badges (Batch A).
                  Client never sees "Booked" or "Paid" language — we
                  translate to: 💡 Advisor Recommendation, ✅ Confirmed,
                  ✨ Included (when Paid). Old `is_prearranged` falls
                  back to a Confirmed badge so legacy items keep their
                  signal until they're re-saved with the new schema. */}
              {(item.is_paid || item.is_booked || item.is_prearranged) && (
                <span
                  className="font-mono text-[9.5px] uppercase tracking-[0.18em] text-[hsl(168,22%,32%)] bg-[hsl(168,22%,32%)]/10 px-1.5 py-0.5 rounded inline-flex items-center gap-1"
                  data-testid={`status-confirmed-${blockKey}-${idx}`}
                  title="Confirmed · this experience has been arranged for you"
                >
                  <span aria-hidden>✅</span> Confirmed
                </span>
              )}
              {item.is_paid && (
                <span
                  className="font-mono text-[9.5px] uppercase tracking-[0.18em] text-[hsl(220,40%,40%)] bg-[hsl(220,40%,40%)]/10 px-1.5 py-0.5 rounded inline-flex items-center gap-1"
                  data-testid={`status-included-${blockKey}-${idx}`}
                  title="Included · already taken care of"
                >
                  <span aria-hidden>✨</span> Included
                </span>
              )}
              {item.is_recommended && !item.is_paid && !item.is_booked && !item.is_prearranged && (
                <span
                  className="font-mono text-[9.5px] uppercase tracking-[0.18em] text-[hsl(var(--accent-gold))] bg-[hsl(var(--accent-gold))]/12 px-1.5 py-0.5 rounded inline-flex items-center gap-1"
                  data-testid={`status-recommended-${blockKey}-${idx}`}
                  title="Curated by your advisor"
                >
                  <span aria-hidden>💡</span> Advisor recommendation
                </span>
              )}
              {advisorRecommended && (
                <span
                  className="font-mono text-[9.5px] uppercase tracking-[0.18em] text-[hsl(var(--accent-gold))] bg-[hsl(var(--accent-gold))]/12 px-1.5 py-0.5 rounded"
                  data-testid={`recommended-chip-${blockKey}-${idx}`}
                  title="Optional · curated by your advisor"
                >
                  Advisor recommended
                </span>
              )}
              {/* Wave 11s — Soft editorial reservation-status badge.
                  Never warning-styled. Calm tones only:
                    • Booked / Reserved / Confirmed → gold (locked-in)
                    • Awaiting Confirmation / Requested / Pending → cream (pending, never red)
                    • Proposed / Planned → sage italic (suggested) */}
              {item.reservation_status && (() => {
                const raw = String(item.reservation_status).trim();
                const lower = raw.toLowerCase();
                let tone = "muted";
                if (["booked", "reserved", "confirmed"].includes(lower)) tone = "gold";
                else if (["awaiting confirmation", "requested", "pending", "awaiting"].includes(lower)) tone = "cream";
                else if (["proposed", "planned", "suggested"].includes(lower)) tone = "sage";
                const toneClasses = {
                  gold: "text-[hsl(var(--accent-gold))] bg-[hsl(var(--accent-gold))]/10",
                  cream: "text-[hsl(var(--foreground-muted))] bg-[hsl(var(--background-paper))] border border-[rgba(0,0,0,0.06)]",
                  sage: "text-[hsl(168,22%,32%)] bg-[hsl(168,22%,32%)]/10",
                  muted: "text-[hsl(var(--foreground-muted))] bg-[hsl(var(--background-paper))]",
                }[tone];
                return (
                  <span
                    className={`font-mono text-[9.5px] uppercase tracking-[0.18em] px-1.5 py-0.5 rounded ${toneClasses}`}
                    data-testid={`reservation-status-${blockKey}-${idx}`}
                    title={`Reservation · ${raw}`}
                  >
                    {raw}
                  </span>
                );
              })()}
            </div>
          </div>
          {/* Wave 11o — Heart-to-save on advisor-recommended option blocks.
              Tap saves to the trip's "NYC folder" the advisor can see. */}
          {isOption && onToggleSave && (
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onToggleSave(item, dayNumber, blockKey, !isSaved);
              }}
              className="ml-1 -mr-1 inline-flex items-center justify-center size-8 rounded-full hover:bg-[hsl(var(--background-paper))] transition-colors"
              title={isSaved ? "Saved · tap to unsave" : "Save to your folder"}
              aria-label={isSaved ? "Unsave" : "Save"}
              data-testid={`save-toggle-${blockKey}-${idx}`}
              data-saved={isSaved ? "true" : "false"}
            >
              <Heart
                className={`size-4 transition-colors ${
                  isSaved
                    ? "fill-[hsl(var(--accent-terracotta))] text-[hsl(var(--accent-terracotta))]"
                    : "text-[hsl(var(--foreground-muted))]"
                }`}
                strokeWidth={1.5}
              />
            </button>
          )}
          <ChevronDown
            className={`size-4 text-[hsl(var(--foreground-muted))] shrink-0 transition-transform duration-500 ${open ? "rotate-180" : ""}`}
            strokeWidth={1.5}
          />
        </div>
      </button>

      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            key="expanded"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.5, ease: EASE_CALM }}
            className="overflow-hidden"
            data-testid={`activity-expanded-${blockKey}-${idx}`}
          >
            <div className="pt-4 sm:pt-5 pb-1 space-y-4">
              {image && (
                <div className="relative aspect-[16/9] sm:aspect-[21/10] rounded-md overflow-hidden bg-[hsl(var(--background-stone))]">
                  <img
                    src={image}
                    alt={title}
                    loading="lazy"
                    className="absolute inset-0 w-full h-full object-cover"
                  />
                </div>
              )}
              {/* Wave 12.z — Important Details banner. Sits at the very
                  top of the expanded view so dress code / reservation
                  name / arrival time hits the reader first. */}
              {importantDetails.length > 0 && (
                <div
                  className="rounded-md border border-[hsl(var(--accent-gold))]/40 bg-[hsl(var(--accent-gold))]/8 px-4 py-3 max-w-2xl"
                  data-testid={`activity-${blockKey}-${idx}-important-banner`}
                >
                  <p className="font-mono text-[9.5px] uppercase tracking-[0.22em] text-[hsl(var(--accent-gold))] mb-1.5">
                    Important
                  </p>
                  <ul className="space-y-1 font-serif text-[13.5px] text-[hsl(var(--foreground))] leading-snug">
                    {importantDetails.map((line, i) => (
                      <li key={i} className="flex gap-2">
                        <span aria-hidden className="text-[hsl(var(--accent-gold))]">·</span>
                        <span>{line}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {/* Wave 11v — Notes (editorial paragraph) leads in the expanded view */}
              {notes && (
                <div className="border-l-2 border-[hsl(var(--accent-gold))]/60 pl-4 py-1 max-w-2xl">
                  <p className="font-accent italic text-[14px] leading-relaxed text-[hsl(var(--foreground-secondary))] whitespace-pre-line">
                    {notes}
                  </p>
                  <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--foreground-muted))]">
                    — From your advisor
                  </p>
                </div>
              )}
              {/* Wave 11v — Detail grid: address (always with map link), website, phone, time range */}
              <dl className="grid sm:grid-cols-2 gap-x-6 gap-y-2.5 max-w-2xl">
                {(time || endTime) && (
                  <div>
                    <dt className="font-mono text-[9.5px] uppercase tracking-[0.22em] text-[hsl(var(--foreground-muted))] mb-0.5">Time</dt>
                    <dd className="font-serif text-[14px] text-[hsl(var(--foreground))] tabular-nums">
                      {time}{endTime && time !== endTime ? ` — ${endTime}` : ""}
                    </dd>
                  </div>
                )}
                {location && (
                  <div className="sm:col-span-2">
                    <dt className="font-mono text-[9.5px] uppercase tracking-[0.22em] text-[hsl(var(--foreground-muted))] mb-0.5">Address</dt>
                    <dd className="font-serif text-[13.5px] text-[hsl(var(--foreground))] flex items-start gap-2">
                      <MapPin className="size-3.5 mt-0.5 shrink-0 text-[hsl(var(--accent-gold))]" strokeWidth={1.5} />
                      <span className="flex-1">{location}</span>
                      {mapsHref && (
                        <a
                          href={mapsHref}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--accent-gold))] hover:underline whitespace-nowrap"
                          data-testid={`activity-maps-${blockKey}-${idx}`}
                        >
                          Map <ArrowUpRight className="size-2.5" strokeWidth={2} />
                        </a>
                      )}
                    </dd>
                  </div>
                )}
                {(website || bookingLink) && (
                  <div>
                    <dt className="font-mono text-[9.5px] uppercase tracking-[0.22em] text-[hsl(var(--foreground-muted))] mb-0.5">Website</dt>
                    <dd>
                      <a
                        href={website || bookingLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 font-serif text-[13.5px] text-[hsl(var(--foreground))] hover:text-[hsl(var(--accent-gold))] transition-colors"
                        data-testid={`activity-website-${blockKey}-${idx}`}
                      >
                        <Globe className="size-3 text-[hsl(var(--accent-gold))]" strokeWidth={1.5} />
                        <span className="truncate max-w-[16rem]">{(website || bookingLink).replace(/^https?:\/\//, "").replace(/\/$/, "")}</span>
                        <ExternalLink className="size-2.5 shrink-0" strokeWidth={2} />
                      </a>
                    </dd>
                  </div>
                )}
                {phone && (
                  <div>
                    <dt className="font-mono text-[9.5px] uppercase tracking-[0.22em] text-[hsl(var(--foreground-muted))] mb-0.5">Phone</dt>
                    <dd>
                      <a
                        href={`tel:${phone.replace(/[^+\d]/g, "")}`}
                        className="inline-flex items-center gap-1.5 font-serif text-[13.5px] text-[hsl(var(--foreground))] hover:text-[hsl(var(--accent-gold))] transition-colors tabular-nums"
                        data-testid={`activity-phone-${blockKey}-${idx}`}
                      >
                        <Phone className="size-3 text-[hsl(var(--accent-gold))]" strokeWidth={1.5} />
                        {phone}
                      </a>
                    </dd>
                  </div>
                )}
                {confirmation && (
                  <div>
                    <dt className="font-mono text-[9.5px] uppercase tracking-[0.22em] text-[hsl(var(--foreground-muted))] mb-0.5">Confirmation</dt>
                    <dd className="font-mono text-[12px] tabular-nums text-[hsl(var(--foreground))] break-all">{confirmation}</dd>
                  </div>
                )}
                {priceEstimate && !costStatus && (
                  <div>
                    <dt className="font-mono text-[9.5px] uppercase tracking-[0.22em] text-[hsl(var(--foreground-muted))] mb-0.5">Estimated</dt>
                    <dd className="font-serif text-[13.5px] text-[hsl(var(--foreground))]">{priceEstimate}</dd>
                  </div>
                )}
                {/* Wave 12.n — Dress code (what to wear) */}
                {item.dress_code && (
                  <div className="sm:col-span-2">
                    <dt className="font-mono text-[9.5px] uppercase tracking-[0.22em] text-[hsl(var(--foreground-muted))] mb-0.5">What to wear</dt>
                    <dd className="font-accent italic text-[13.5px] text-[hsl(var(--foreground))]" data-testid={`activity-dress-code-${blockKey}-${idx}`}>
                      {item.dress_code}
                    </dd>
                  </div>
                )}
              </dl>
              {/* Wave 12.ab — Transportation block. Renders only when
                  visibility/status allow it; surprise events auto-strip
                  destination + provider details client-side. */}
              {(() => {
                const isSurprise = Boolean(item.is_surprise || item.surprise || item.surprise_until);
                const t = resolveTransportDisplay(item.transportation, { isSurprise, isAdvisorView: false, itemName: item.name });
                if (!t.allowed) return null;
                const Icon = t.icon;
                return (
                  <div
                    className="rounded-md border border-[hsl(var(--accent-gold))]/25 bg-[hsl(var(--accent-gold))]/6 px-4 py-3 max-w-2xl flex items-start gap-3"
                    data-testid={`activity-${blockKey}-${idx}-transport`}
                  >
                    {Icon ? <Icon className="size-4 text-[hsl(var(--accent-gold))] mt-0.5 shrink-0" strokeWidth={1.5} /> : null}
                    <div className="flex-1 min-w-0">
                      <p className="font-mono text-[9.5px] uppercase tracking-[0.22em] text-[hsl(var(--accent-gold))]">
                        {t.eyebrow}
                      </p>
                      {t.body ? (
                        <p className="font-serif text-[13.5px] text-[hsl(var(--foreground))] mt-0.5 leading-snug">
                          {t.body}
                        </p>
                      ) : null}
                      {t.sub ? (
                        <p className="font-mono text-[11px] text-[hsl(var(--foreground-muted))] mt-0.5">
                          {t.sub}
                        </p>
                      ) : null}
                    </div>
                  </div>
                );
              })()}
              {/* Wave 12.z — Reservation Details (collapsible). Operational
                  data the client may want during the moment but doesn't
                  need crowding the editorial layout. */}
              {reservationDetails.length > 0 && (
                <div
                  className="border-t border-[rgba(0,0,0,0.06)] pt-3"
                  data-testid={`activity-${blockKey}-${idx}-reservation-block`}
                >
                  <button
                    type="button"
                    onClick={() => setReservationOpen((v) => !v)}
                    className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--foreground-muted))] hover:text-[hsl(var(--foreground))]"
                    aria-expanded={reservationOpen}
                    data-testid={`activity-${blockKey}-${idx}-reservation-toggle`}
                  >
                    <span>{reservationOpen ? "Hide" : "View"} reservation details</span>
                    <span aria-hidden className={`transition-transform ${reservationOpen ? "rotate-90" : ""}`}>›</span>
                  </button>
                  {reservationOpen && (
                    <ul
                      className="mt-2.5 space-y-1.5 font-mono text-[12px] text-[hsl(var(--foreground-secondary))] max-w-2xl"
                      data-testid={`activity-${blockKey}-${idx}-reservation-list`}
                    >
                      {reservationDetails.map((line, i) => (
                        <li key={i} className="break-all">{line}</li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
              {/* Wave 26.25 · Phase 3c — full cost line in expanded view. */}
              {costStatus && (
                <CostStatusLine
                  status={costStatus}
                  amount={costAmount}
                  currency={costCurrency}
                  notes={costNotes}
                />
              )}
              {/* Wave 11v — Primary booking/website CTA at the bottom */}
              {bookingLink && (
                <a
                  href={bookingLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-[hsl(var(--accent-gold))]/10 text-[hsl(var(--accent-gold))] hover:bg-[hsl(var(--accent-gold))] hover:text-[hsl(var(--background))] transition-all font-mono text-[10.5px] uppercase tracking-[0.2em] border border-[hsl(var(--accent-gold))]/30"
                  data-testid={`activity-booking-cta-${blockKey}-${idx}`}
                >
                  Open booking
                  <ExternalLink className="size-3" strokeWidth={2} />
                </a>
              )}
              {/* Wave 11w · Preference Intelligence — reaction + review note */}
              {tripId && !isOption && (
                <ReactionBlock
                  item={item}
                  dayNumber={dayNumber}
                  blockKey={blockKey}
                  tripId={tripId}
                />
              )}
              {/* Wave 26.27 · Phase 3e — Smart cross-section deep link.
                  Routes the member from the chronological flow to the
                  operational sanctuary at the right shelf. */}
              {tripId && (() => {
                const target = pickEssentialsTarget(item, blockKey);
                return (
                  <Link
                    to={`/portal/client/experiences/${tripId}/essentials#${target.anchor}`}
                    className="inline-flex items-center gap-1.5 mt-1 font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--foreground-muted))] hover:text-[hsl(var(--accent-gold))] transition-colors"
                    data-testid={`activity-essentials-link-${blockKey}-${idx}`}
                    data-essentials-anchor={target.anchor}
                  >
                    {target.label}
                    <ChevronDown className="size-3 -rotate-90" strokeWidth={1.5} />
                  </Link>
                );
              })()}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// ActivityTimeline — continuous vertical flow with soft block markers.
// ═══════════════════════════════════════════════════════════════════
const BLOCK_ORDER = [
  { key: "morning",   label: "Morning" },
  { key: "afternoon", label: "Afternoon" },
  { key: "evening",   label: "Evening" },
];

function collectBlockItems(block) {
  if (!block) return [];
  if (Array.isArray(block)) return _sortByTime(block);
  if (Array.isArray(block.items)) return _sortByTime(block.items);
  if (typeof block === "object") {
    const items = [];
    ["meal_slot", "primary_activity", "secondary_activity"].forEach((s) => {
      if (block[s]) items.push(block[s]);
    });
    // Wave 11o — tag extras so the card knows they're "advisor recommended"
    // option blocks. Heart/save is enabled only for these.
    if (Array.isArray(block.extra_items)) {
      block.extra_items.forEach((e) => {
        items.push({ ...e, _is_option: true, _advisor_recommended: true });
      });
    }
    return _sortByTime(items);
  }
  return [];
}

// Wave 12.m — Chronological order on the client itinerary. A transfer
// scheduled for 5:20 PM must surface BEFORE the 6:00 PM dinner it's
// driving to. Items with no time fall to the bottom in their original
// (slot) order so the block doesn't shuffle unexpectedly.
function _sortByTime(items) {
  const withIdx = items.map((it, i) => ({ it, i, mins: _timeToMins(it?.time) }));
  withIdx.sort((a, b) => {
    if (a.mins === null && b.mins === null) return a.i - b.i;
    if (a.mins === null) return 1;
    if (b.mins === null) return -1;
    return a.mins - b.mins;
  });
  return withIdx.map((x) => x.it);
}
function _timeToMins(t) {
  if (!t || typeof t !== "string") return null;
  const s = t.trim();
  // Match "6:00 PM", "06:00", "18:00", "6 PM" — case-insensitive
  const m = s.match(/^(\d{1,2})(?::(\d{2}))?\s*(am|pm)?$/i);
  if (!m) return null;
  let hr = parseInt(m[1], 10);
  const min = m[2] ? parseInt(m[2], 10) : 0;
  const ampm = (m[3] || "").toLowerCase();
  if (ampm === "pm" && hr < 12) hr += 12;
  if (ampm === "am" && hr === 12) hr = 0;
  if (hr > 23 || min > 59) return null;
  return hr * 60 + min;
}

function ActivityTimeline({ day, focusedKey, onFocusChange, tripId, savedSet, onToggleSave, destinationTz }) {
  // Flatten to (blockKey, items[]) so we can interleave block markers
  // between activities in chronological flow.
  const flow = BLOCK_ORDER
    .map((b) => ({ key: b.key, label: b.label, items: collectBlockItems(day[b.key]) }))
    .filter((b) => b.items.length > 0);

  // ────────────────────────────────────────────────────────────────
  // Wave 12.j — Live time awareness on TODAY's day.
  // For each block, decide if it has fully ended (all items' times
  // are in the past) in the destination's local clock. Past blocks
  // collapse behind a single "Earlier today · 3 events ↓" affordance
  // so the client doesn't scroll through history to find now.
  // Bumps every 60s via `tick` so the boundary shifts naturally.
  // ────────────────────────────────────────────────────────────────
  const [tick, setTick] = React.useState(0);
  React.useEffect(() => {
    const t = setInterval(() => setTick((n) => n + 1), 60_000);
    return () => clearInterval(t);
  }, []);

  const tz = destinationTz || "America/New_York";
  const isToday = React.useMemo(() => {
    try {
      const today = new Intl.DateTimeFormat("en-CA", {
        timeZone: tz, year: "numeric", month: "2-digit", day: "2-digit",
      }).format(new Date());
      return (day.date || "").slice(0, 10) === today;
    } catch { return false; }
  }, [day.date, tz, tick]); // eslint-disable-line react-hooks/exhaustive-deps

  const nowMin = React.useMemo(() => {
    try {
      const parts = new Intl.DateTimeFormat("en-US", {
        timeZone: tz, hour: "numeric", minute: "numeric", hour12: false,
      }).formatToParts(new Date());
      return parseInt(parts.find((p) => p.type === "hour").value, 10) * 60 +
             parseInt(parts.find((p) => p.type === "minute").value, 10);
    } catch {
      const d = new Date();
      return d.getHours() * 60 + d.getMinutes();
    }
  }, [tz, tick]);

  const parseTime = (s) => {
    if (!s) return null;
    const m = String(s).trim().match(/(\d{1,2}):(\d{2})\s*(am|pm|AM|PM)?/);
    if (!m) return null;
    let h = parseInt(m[1], 10);
    const mm = parseInt(m[2], 10);
    const ampm = (m[3] || "").toLowerCase();
    if (ampm === "pm" && h < 12) h += 12;
    if (ampm === "am" && h === 12) h = 0;
    return h * 60 + mm;
  };
  const isItemPast = (it) => {
    const t = parseTime(it?.time || it?.end_time);
    return t != null && t < nowMin;
  };
  const blockIsPast = (blk) => isToday && blk.items.length > 0 && blk.items.every(isItemPast);

  const pastBlocks = flow.filter(blockIsPast);
  const remainingBlocks = flow.filter((b) => !blockIsPast(b));
  const [collapsed, setCollapsed] = React.useState(true);
  // When the user opens "Earlier today", remember it so it doesn't
  // collapse again on every tick.
  const totalPastItems = pastBlocks.reduce((n, b) => n + b.items.length, 0);

  if (flow.length === 0) return null;

  const renderBlock = (blk, dim = false) => (
    <div key={blk.key} className={`space-y-5 sm:space-y-6 ${dim ? "opacity-60" : ""}`}>
      <div
        className="pl-9 sm:pl-12 flex items-baseline gap-2"
        data-testid={`block-marker-${blk.key}-day-${day.day_number}`}
      >
        <span className="font-mono text-[10px] uppercase tracking-[0.28em] text-[hsl(var(--foreground-muted))]">
          {blk.label}
        </span>
        <span aria-hidden className="flex-1 h-px bg-[rgba(0,0,0,0.05)] ml-1" />
      </div>
      {blk.items.map((it, i) => (
        it && it.item_type === "option_block" ? (
          <OptionBlockCard
            key={it?.id || `${blk.key}-${i}-opt`}
            item={it}
            idx={i}
            blockKey={blk.key}
            dayNumber={day.day_number}
            tripId={tripId}
          />
        ) : (
          <ActivityCard
            key={it?.id || `${blk.key}-${i}`}
            item={it}
            idx={i}
            blockKey={blk.key}
            dayNumber={day.day_number}
            focusKey={`${blk.key}-${i}`}
            focusedKey={focusedKey}
            onFocusChange={onFocusChange}
            tripId={tripId}
            savedSet={savedSet}
            onToggleSave={onToggleSave}
          />
        )
      ))}
    </div>
  );

  return (
    <div
      className="relative space-y-7 sm:space-y-9"
      data-testid={`activity-timeline-day-${day.day_number}`}
    >
      {/* Soft vertical line behind the numbered dots */}
      <span
        aria-hidden
        className="absolute left-[14px] sm:left-[18px] top-3 bottom-3 w-px bg-gradient-to-b from-[rgba(0,0,0,0.08)] via-[rgba(0,0,0,0.04)] to-transparent"
      />
      {/* Earlier-today affordance — only on today's day, only if 1+
          full block has already passed. Collapsed by default; one tap
          to reveal. */}
      {isToday && pastBlocks.length > 0 && totalPastItems > 0 && (
        collapsed ? (
          <button
            type="button"
            onClick={() => setCollapsed(false)}
            className="mx-9 sm:mx-12 rounded-md border border-dashed border-[hsl(var(--accent-gold))]/40 bg-[hsl(var(--background-stone))]/40 px-4 py-2.5 flex items-center gap-2 text-left hover:bg-[hsl(var(--background-stone))]/70 transition-colors"
            data-testid={`earlier-today-toggle-day-${day.day_number}`}
            aria-expanded="false"
          >
            <ChevronDown className="size-3.5 text-[hsl(var(--accent-gold))]" strokeWidth={1.5} />
            <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--foreground-muted))]">
              Earlier today
            </span>
            <span className="font-accent italic text-[12px] text-[hsl(var(--foreground-muted))]">
              {totalPastItems} event{totalPastItems === 1 ? "" : "s"} · tap to reveal
            </span>
          </button>
        ) : (
          <>
            <div className="mx-9 sm:mx-12 -mb-3 flex items-center justify-between">
              <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--foreground-muted))]">
                Earlier today
              </span>
              <button
                type="button"
                onClick={() => setCollapsed(true)}
                className="font-mono text-[9.5px] uppercase tracking-[0.2em] text-[hsl(var(--foreground-muted))] hover:text-[hsl(var(--accent-gold))]"
                data-testid={`earlier-today-collapse-day-${day.day_number}`}
              >
                Collapse ↑
              </button>
            </div>
            {pastBlocks.map((blk) => renderBlock(blk, true))}
          </>
        )
      )}
      {remainingBlocks.map((blk) => renderBlock(blk))}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// DayChapter — one full editorial chapter per day.
// Wave 26.24 · Phase 3b — owns the cross-highlight `focusedKey` state
// shared between its ActivityTimeline and its RouteMap.
// ═══════════════════════════════════════════════════════════════════
function DayChapter({ day, index, veiled, tripId, savedSet, onToggleSave, destinationTz }) {
  const isLocked = day.visibility === "scheduled_reveal" && day.scheduled_reveal_at && new Date(day.scheduled_reveal_at) > new Date();
  const isHidden = !day.visibility || day.visibility === "hidden";
  const dayDate = day.date ? fmt.formatDate(day.date, { weekday: "long", month: "long", day: "numeric" }) : null;

  // Veiled trips: the day-level destination name must NOT leak.
  // Replace destination_name with a quiet placeholder, suppress the
  // map (lat/lng would also leak), and gate the WeatherChip.
  const hideDestination = veiled;
  // Wave 11k — when the advisor has authored an editorial chapter
  // title (e.g. "Wellness, Broadway & Midnight Manhattan"), that wins
  // the hero. The destination_name falls into the eyebrow so the city
  // is still surfaced quietly above.
  const dayTitle = hideDestination
    ? (day.day_of_week || `Day ${day.day_number}`)
    : (day.day_title || day.destination_name || day.day_of_week || `Day ${day.day_number}`);
  const dayEyebrowCity = !hideDestination && day.day_title && day.destination_name && day.day_title !== day.destination_name
    ? day.destination_name
    : null;

  // Shared cross-highlight state — wires the timeline ↔ map together.
  const [focusedKey, setFocusedKey] = React.useState(null);
  const geoPoints = React.useMemo(() => collectGeocodedPoints(day), [day]);
  const hasMap = !hideDestination && !isHidden && !isLocked && geoPoints.length > 0;

  // ─ Secret-day card (preserved) ─
  if (isHidden || isLocked) {
    let unlockLabel = "Your advisor is shaping this day";
    if (isLocked) {
      const unlockAt = new Date(day.scheduled_reveal_at);
      const hoursUntil = (unlockAt - new Date()) / (1000 * 60 * 60);
      const timeStr = unlockAt.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
      if (hoursUntil < 6) unlockLabel = `Unlocks in a few hours · ${timeStr}`;
      else if (hoursUntil < 24) unlockLabel = `Unlocks tonight at ${timeStr}`;
      else unlockLabel = `Unlocks ${fmt.formatDate(day.scheduled_reveal_at, { weekday: "short", month: "short", day: "numeric" })} · ${timeStr}`;
    }
    return (
      <motion.section
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.55, delay: Math.min(index, 4) * 0.05, ease: EASE_CALM }}
        id={`day-${day.day_number}`}
        data-testid={`day-${day.day_number}-locked`}
        className="relative aspect-[5/6] sm:aspect-[21/9] rounded-md overflow-hidden bg-[hsl(20,12%,12%)]"
      >
        <div className="absolute inset-0 bg-gradient-to-br from-[hsl(28,18%,18%)] via-[hsl(20,12%,10%)] to-[hsl(20,12%,8%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_rgba(212,175,55,0.18)_0%,_transparent_60%)]" />
        <div className="absolute inset-x-0 bottom-0 p-7 sm:p-10 text-white space-y-3">
          <div className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.28em] opacity-70">
            <Lock className="size-3" strokeWidth={1.5} />
            Day {day.day_number}{dayDate ? ` · ${dayDate}` : ""}
          </div>
          <p className="font-serif text-[24px] sm:text-[30px] leading-snug tracking-tight max-w-xl">
            {day.teaser_text || "A quiet surprise is taking shape."}
          </p>
          <p className="font-accent italic text-[14px] sm:text-[15px] opacity-80 max-w-md">
            {unlockLabel}
          </p>
        </div>
      </motion.section>
    );
  }

  // ─ Open editorial chapter ─
  return (
    <motion.section
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, delay: Math.min(index, 4) * 0.06, ease: EASE_CALM }}
      id={`day-${day.day_number}`}
      data-testid={`day-${day.day_number}`}
      className="space-y-7 sm:space-y-9"
    >
      {/* Day hero */}
      <div className="relative overflow-hidden rounded-md -mx-5 sm:mx-0 aspect-[5/6] sm:aspect-[21/9] bg-[hsl(var(--background-stone))]">
        {day.day_image_url && !hideDestination ? (
          <img
            src={day.day_image_url}
            alt={dayTitle}
            loading={index < 2 ? "eager" : "lazy"}
            className="absolute inset-0 w-full h-full object-cover"
            data-testid={`day-${day.day_number}-hero-image`}
          />
        ) : (
          <div className="absolute inset-0 bg-gradient-to-br from-[hsl(var(--background-paper))] via-[hsl(var(--background-stone))] to-[hsl(var(--background-paper))]" />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/65 via-black/15 to-transparent" />

        <div className="absolute inset-x-0 bottom-0 p-6 sm:p-10 text-white space-y-2.5">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <p className="font-mono text-[10px] uppercase tracking-[0.28em] opacity-85">
              Day {day.day_number}{dayDate ? ` · ${dayDate}` : ""}{dayEyebrowCity ? ` · ${dayEyebrowCity}` : ""}
            </p>
            <div className="flex items-center gap-2">
              {day.is_arrival_day && (
                <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--accent-gold))] bg-black/30 backdrop-blur-sm border border-[hsl(var(--accent-gold))]/40 px-2 py-0.5 rounded-full">
                  Arrival
                </span>
              )}
              {day.is_departure_day && (
                <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--accent-gold))] bg-black/30 backdrop-blur-sm border border-[hsl(var(--accent-gold))]/40 px-2 py-0.5 rounded-full">
                  Departure
                </span>
              )}
              {!hideDestination && day.destination_name && <WeatherChip destination={day.destination_name} date={day.date} />}
            </div>
          </div>
          <h3 className="font-serif text-[26px] sm:text-[36px] lg:text-[44px] leading-[1.05] tracking-tight">
            {dayTitle}
          </h3>
          {day.day_subheading && (
            <p className="font-accent italic text-[14px] sm:text-[17px] opacity-90 max-w-xl">
              {day.day_subheading}
            </p>
          )}
        </div>
      </div>

      {/* Wave 11m · Priority note — concierge update that should land
          before anything else on the day. Quiet sage chip, never loud. */}
      {day.priority_note && (
        <div
          className="rounded-md border border-[hsl(var(--accent-gold))]/30 bg-[hsl(var(--accent-gold))]/8 px-4 py-3 flex items-start gap-2.5 max-w-2xl"
          data-testid={`day-priority-note-${day.day_number}`}
        >
          <Sparkles className="size-4 mt-0.5 shrink-0 text-[hsl(var(--accent-gold))]" strokeWidth={1.5} />
          <p className="font-accent italic text-[14.5px] leading-relaxed text-[hsl(var(--foreground-secondary))]">
            {day.priority_note}
          </p>
        </div>
      )}

      {/* Day intro prose (advisor's curated notes) */}
      {day.day_notes && (
        <div className="border-l-2 border-[hsl(var(--accent-gold))]/40 pl-5 sm:pl-6 max-w-2xl">
          <p className="font-accent italic text-[15.5px] sm:text-[16px] leading-relaxed text-[hsl(var(--foreground-secondary))] whitespace-pre-line">
            {day.day_notes}
          </p>
        </div>
      )}

      {/* Rest-day quiet message */}
      {day.is_rest_day && (
        <p className="font-accent italic text-[15px] sm:text-[16px] leading-relaxed text-[hsl(var(--foreground-secondary))] max-w-xl">
          {day.rest_message || "A quiet day. Nothing planned — wander as you wish."}
        </p>
      )}

      {/* Two-column layout on desktop: timeline (left) · sticky route map
          (right). Mobile collapses to a single column with the map peek
          rendered inline beneath the timeline. */}
      {!day.is_rest_day && (
        <div className={hasMap ? "lg:grid lg:grid-cols-[1fr_340px] lg:gap-10 lg:items-start" : ""}>
          <ActivityTimeline day={day} focusedKey={focusedKey} onFocusChange={setFocusedKey} tripId={tripId} savedSet={savedSet} onToggleSave={onToggleSave} destinationTz={destinationTz} />
          {hasMap && (
            <aside
              className="hidden lg:block lg:sticky lg:top-[88px]"
              data-testid={`day-${day.day_number}-route-rail`}
            >
              <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-[hsl(var(--foreground-muted))] mb-3">
                Day route · {geoPoints.length} stop{geoPoints.length === 1 ? "" : "s"}
              </p>
              <RouteMap day={day} focusedKey={focusedKey} onFocusChange={setFocusedKey} />
            </aside>
          )}
        </div>
      )}

      {/* Mobile-only collapsible map (hidden on lg+) */}
      {hasMap && (
        <div className="lg:hidden">
          <RouteMobilePeek day={day} focusedKey={focusedKey} onFocusChange={setFocusedKey} />
        </div>
      )}
    </motion.section>
  );
}

// ═══════════════════════════════════════════════════════════════════
// RouteMobilePeek — collapsed-by-default route pill for mobile.
// Desktop has the sticky right-rail; on mobile we keep the calm
// "Show route" expandable below the timeline.
// ═══════════════════════════════════════════════════════════════════
function RouteMobilePeek({ day, focusedKey, onFocusChange }) {
  const [open, setOpen] = React.useState(false);
  const geoPoints = collectGeocodedPoints(day);
  if (geoPoints.length === 0) return null;
  return (
    <div data-testid={`day-${day.day_number}-route-peek`}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full border border-[rgba(0,0,0,0.08)] hover:border-[hsl(var(--accent-gold))]/50 hover:text-[hsl(var(--foreground))] transition-colors font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--foreground-muted))]"
        aria-expanded={open}
      >
        <MapPin className="size-3" strokeWidth={1.5} />
        {open ? "Hide route" : `Show route · ${geoPoints.length} stop${geoPoints.length === 1 ? "" : "s"}`}
        <ChevronDown className={`size-3 transition-transform duration-500 ${open ? "rotate-180" : ""}`} strokeWidth={1.5} />
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.55, ease: EASE_CALM }}
            className="overflow-hidden mt-4"
          >
            <RouteMap day={day} focusedKey={focusedKey} onFocusChange={onFocusChange} />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// Page
// ═══════════════════════════════════════════════════════════════════
// ═══════════════════════════════════════════════════════════════════
// Wave 11o — Saved folder card. Quiet summary of the client's hearted
// options, grouped by destination. Only renders when non-empty.
// ═══════════════════════════════════════════════════════════════════
function SavedFolderCard({ folders, onUnsave, tripId }) {
  const total = folders.reduce((s, f) => s + (f.items?.length || 0), 0);
  return (
    <div
      className="rounded-lg border border-[hsl(var(--accent-terracotta))]/20 bg-[hsl(var(--accent-terracotta))]/5 px-5 sm:px-6 py-5 space-y-4"
      data-testid="saved-folder-card"
    >
      <div className="flex items-center gap-2 flex-wrap">
        <Heart className="size-4 text-[hsl(var(--accent-terracotta))] fill-[hsl(var(--accent-terracotta))]" strokeWidth={1.5} />
        <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--accent-terracotta))]">
          Your folder · {total} saved
        </p>
      </div>
      <p className="font-accent italic text-[14px] text-[hsl(var(--foreground-secondary))] max-w-xl leading-relaxed">
        Optional choices you've hearted. Your advisor sees this list and can plan around it.
      </p>
      <div className="space-y-3">
        {folders.map((f) => (
          <div key={f.destination_name} data-testid={`saved-folder-${f.destination_name}`}>
            <p className="font-mono text-[9.5px] uppercase tracking-[0.22em] text-[hsl(var(--foreground-muted))] mb-1.5">
              {f.destination_name}
            </p>
            <div className="flex flex-wrap gap-2">
              {(f.items || []).map((it) => (
                <button
                  key={it.id}
                  type="button"
                  onClick={() => onUnsave(it)}
                  className="group inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-white/70 border border-[rgba(0,0,0,0.06)] hover:border-[hsl(var(--accent-terracotta))]/40 transition-colors"
                  title="Tap to unsave"
                  data-testid={`saved-folder-item-${it.item_id}`}
                >
                  <Heart className="size-3 fill-[hsl(var(--accent-terracotta))] text-[hsl(var(--accent-terracotta))] group-hover:opacity-50" strokeWidth={1.5} />
                  <span className="font-accent italic text-[13px] text-[hsl(var(--foreground-secondary))]">
                    {it.snapshot?.name || "Saved"}
                  </span>
                  <span className="font-mono text-[9px] uppercase tracking-[0.18em] text-[hsl(var(--foreground-muted))]">
                    Day {it.day_number}
                  </span>
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════
// Wave 11u · Per-day routing
// ───────────────────────────────────────────────────────────────────
// The itinerary is now structured as: an index of days (calm cards with
// hero thumbnails) at `/itinerary`, and a focused single-day view at
// `/itinerary/day/:dayNumber` (hero + DayChapter + prev/next pager).
// Both routes share the same component below — branch on params.
// ═══════════════════════════════════════════════════════════════════

// ═══════════════════════════════════════════════════════════════════
// Wave 11x — NextUnlockCard
// ───────────────────────────────────────────────────────────────────
// A single calm placeholder shown at the end of the day-index list
// when the client has one or more future days still on the time-gate
// (auto-unlock at 10 PM destination-local the night before).
//
// Backend sends ONE `is_next_unlock` entry — the soonest locked day —
// with `unlock_label` ("10 PM ET · June 2") and the precise UTC ISO
// for client-side countdown. No other locked days appear in the list;
// they remain entirely hidden until their own gate passes.
// ═══════════════════════════════════════════════════════════════════
function NextUnlockCard({ day }) {
  const [now, setNow] = React.useState(() => new Date());
  React.useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 60_000); // refresh every minute
    return () => clearInterval(t);
  }, []);

  const unlockAt = day.unlock_at_utc ? new Date(day.unlock_at_utc) : null;
  const minutesUntil = unlockAt ? Math.max(0, Math.round((unlockAt - now) / 60_000)) : null;

  let countdown = null;
  if (minutesUntil !== null) {
    if (minutesUntil < 60) {
      countdown = minutesUntil <= 1 ? "Unlocking momentarily" : `Unlocks in ${minutesUntil} minutes`;
    } else if (minutesUntil < 24 * 60) {
      const h = Math.round(minutesUntil / 60);
      countdown = `Unlocks in ${h} hour${h === 1 ? "" : "s"}`;
    }
  }

  // Wave 11x.1 — Luxury wording. Title is calm and declarative; the
  // advisor-authored teaser_text (poetic stanza, multi-line) carries the
  // emotion below. unlock_label format: "10 PM EDT · June 2".
  const titleLine = `Day ${day.day_number} will unlock at ${day.unlock_label || "10 PM the night before"}.`;
  const teaser = (day.teaser_text || "").trim();

  return (
    <motion.div
      variants={fadeInUp}
      className="sm:col-span-2 relative rounded-md overflow-hidden bg-gradient-to-br from-[hsl(28,18%,16%)] via-[hsl(20,12%,10%)] to-[hsl(20,12%,8%)]"
      data-testid={`next-unlock-card-day-${day.day_number}`}
    >
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_rgba(212,175,55,0.20)_0%,_transparent_55%)]" />
      <div className="relative px-6 py-7 sm:px-10 sm:py-10 text-white">
        <div className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.28em] opacity-70 mb-3">
          <Lock className="size-3" strokeWidth={1.5} /> The next chapter
        </div>
        <p
          className="font-serif text-[22px] sm:text-[26px] leading-snug tracking-tight max-w-xl"
          data-testid="next-unlock-message"
        >
          {titleLine}
        </p>
        {teaser && (
          <p
            className="font-accent italic text-[16px] sm:text-[18px] leading-relaxed opacity-90 mt-5 whitespace-pre-line max-w-lg"
            data-testid="next-unlock-teaser"
          >
            {teaser}
          </p>
        )}
        {countdown && (
          <p
            className="font-accent italic text-[14px] sm:text-[15px] opacity-70 mt-5"
            data-testid="next-unlock-countdown"
          >
            {countdown}
          </p>
        )}
        <p className="font-mono text-[10px] uppercase tracking-[0.22em] opacity-50 mt-6">
          Every chapter quietly arrives at 10 PM the night before
        </p>
      </div>
    </motion.div>
  );
}


function DayIndexCard({ day, tripId, isPreview, base, index }) {
  const isLocked = day.visibility === "scheduled_reveal" && day.scheduled_reveal_at && new Date(day.scheduled_reveal_at) > new Date();
  const isHidden = !day.visibility || day.visibility === "hidden";
  const dayDate = day.date ? fmt.formatDate(day.date, { weekday: "long", month: "long", day: "numeric" }) : null;
  const dayShortDate = day.date ? fmt.formatDate(day.date, { weekday: "short", month: "short", day: "numeric" }) : null;
  const hero = day.day_image_url || day.hero_image_url || "";
  const href = isPreview
    ? `${base}/itinerary/day/${day.day_number}`
    : `/portal/client/experiences/${tripId}/itinerary/day/${day.day_number}`;

  // Locked / hidden — quiet placeholder card (no hero leak)
  if (isHidden || isLocked) {
    let label = "Your advisor is shaping this day";
    if (isLocked) {
      const unlockAt = new Date(day.scheduled_reveal_at);
      const hoursUntil = (unlockAt - new Date()) / (1000 * 60 * 60);
      if (hoursUntil < 24) label = `Unlocks ${unlockAt.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })}`;
      else label = `Unlocks ${dayShortDate || ""}`;
    }
    return (
      <motion.div
        variants={fadeInUp}
        className="relative aspect-[5/4] sm:aspect-[16/9] rounded-md overflow-hidden bg-gradient-to-br from-[hsl(28,18%,18%)] via-[hsl(20,12%,10%)] to-[hsl(20,12%,8%)]"
        data-testid={`day-index-card-${day.day_number}-locked`}
      >
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_rgba(212,175,55,0.18)_0%,_transparent_60%)]" />
        <div className="absolute inset-x-0 bottom-0 p-5 sm:p-7 text-white">
          <div className="inline-flex items-center gap-1.5 font-mono text-[9.5px] uppercase tracking-[0.26em] opacity-70 mb-2">
            <Lock className="size-3" strokeWidth={1.5} /> Day {day.day_number}{dayShortDate ? ` · ${dayShortDate}` : ""}
          </div>
          <p className="font-accent italic text-[15px] sm:text-[17px] opacity-90">{label}</p>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div variants={fadeInUp}>
      <Link
        to={href}
        className="group relative block aspect-[5/4] sm:aspect-[16/9] rounded-md overflow-hidden bg-[hsl(var(--background-paper))] focus:outline-none focus:ring-2 focus:ring-[hsl(var(--accent-gold))]"
        data-testid={`day-index-card-${day.day_number}`}
      >
        {hero && (
          <img
            src={hero}
            alt={day.day_title || `Day ${day.day_number}`}
            loading="lazy"
            className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.03]"
          />
        )}
        {/* Editorial scrim */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
        <div className="absolute inset-x-0 bottom-0 p-5 sm:p-7 text-white">
          <div className="font-mono text-[9.5px] uppercase tracking-[0.26em] opacity-75 mb-1.5">
            Day {day.day_number}{dayShortDate ? ` · ${dayShortDate}` : ""}
          </div>
          <h3 className="font-serif text-[20px] sm:text-[26px] leading-tight">
            {day.day_title || day.destination_name || `Day ${day.day_number}`}
          </h3>
          {day.day_subheading && (
            <p className="mt-1 font-accent italic text-[13px] sm:text-[14.5px] opacity-90 line-clamp-2">
              {day.day_subheading}
            </p>
          )}
        </div>
        <div className="absolute top-3 right-3 sm:top-4 sm:right-4 inline-flex items-center gap-1 px-2 py-1 rounded-full bg-black/40 backdrop-blur-sm font-mono text-[9.5px] uppercase tracking-[0.22em] text-white opacity-0 group-hover:opacity-100 transition-opacity">
          Open <ChevronRight className="size-3" strokeWidth={1.8} />
        </div>
      </Link>
    </motion.div>
  );
}

function DayPager({ days, currentDayNumber, tripId, isPreview, base }) {
  const idx = days.findIndex((d) => d.day_number === currentDayNumber);
  const prev = idx > 0 ? days[idx - 1] : null;
  const next = idx < days.length - 1 ? days[idx + 1] : null;
  const linkBase = isPreview ? `${base}/itinerary` : `/portal/client/experiences/${tripId}/itinerary`;
  const indexHref = linkBase;

  return (
    <div className="mt-12 pt-8 border-t border-[rgba(0,0,0,0.06)]">
      <div className="grid grid-cols-2 gap-3 sm:gap-4">
        {prev ? (
          <Link
            to={`${linkBase}/day/${prev.day_number}`}
            className="group flex items-center gap-3 p-4 rounded-md border border-[rgba(0,0,0,0.06)] bg-white hover:border-[hsl(var(--accent-gold))]/40 transition-colors"
            data-testid="day-pager-prev"
          >
            <ChevronLeft className="size-4 text-[hsl(var(--foreground-muted))] group-hover:text-[hsl(var(--accent-gold))]" strokeWidth={1.5} />
            <div className="flex-1 min-w-0 text-left">
              <p className="font-mono text-[9.5px] uppercase tracking-[0.26em] text-[hsl(var(--foreground-muted))] mb-0.5">Day {prev.day_number}</p>
              <p className="font-serif text-[13.5px] truncate">{prev.day_title || prev.destination_name || "Previous day"}</p>
            </div>
          </Link>
        ) : <div />}

        {next ? (
          <Link
            to={`${linkBase}/day/${next.day_number}`}
            className="group flex items-center gap-3 p-4 rounded-md border border-[rgba(0,0,0,0.06)] bg-white hover:border-[hsl(var(--accent-gold))]/40 transition-colors text-right"
            data-testid="day-pager-next"
          >
            <div className="flex-1 min-w-0 text-right">
              <p className="font-mono text-[9.5px] uppercase tracking-[0.26em] text-[hsl(var(--foreground-muted))] mb-0.5">Day {next.day_number}</p>
              <p className="font-serif text-[13.5px] truncate">{next.day_title || next.destination_name || "Next day"}</p>
            </div>
            <ChevronRight className="size-4 text-[hsl(var(--foreground-muted))] group-hover:text-[hsl(var(--accent-gold))]" strokeWidth={1.5} />
          </Link>
        ) : <div />}
      </div>
      <div className="mt-4 text-center">
        <Link
          to={indexHref}
          className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--foreground-muted))] hover:text-[hsl(var(--accent-gold))] transition-colors"
          data-testid="day-pager-index"
        >
          <CalendarDays className="size-3" strokeWidth={1.5} />
          All days
        </Link>
      </div>
    </div>
  );
}

export default function ClientItinerary() {
  const params = useParams();
  const navigate = useNavigate();
  const { isPreview, base } = usePreviewBase();
  // Inside the new ExperienceLayout shell, the breadcrumb + section
  // pills already handle navigation. Detect that context to skip the
  // duplicate "Trip home" back link.
  const ctx = useOutletContext();
  const insideExperienceShell = Boolean(ctx?.trip);
  const trip = ctx?.trip || null;
  const veil = useDestinationVeil(trip);

  const tripId = params.tripId || params.innerTripId;
  const backHref = isPreview ? base : `/portal/client/experiences/${tripId}`;
  // Wave 11u — single-day route: `/itinerary/day/:dayNumber`
  const requestedDayNumber = params.dayNumber ? Number(params.dayNumber) : null;
  const isDayView = requestedDayNumber !== null && !Number.isNaN(requestedDayNumber);
  const indexHref = isPreview ? `${base}/itinerary` : `/portal/client/experiences/${tripId}/itinerary`;

  const [days, setDays] = React.useState(null);
  const [error, setError] = React.useState(null);
  // Wave 12.o — Important Travel Ops notes for the LiveDayBanner.
  const [importantOps, setImportantOps] = React.useState([]);
  React.useEffect(() => {
    if (!tripId) return;
    api.get(`/client-portal/experiences/${tripId}/important-ops`)
      .then(({ data }) => setImportantOps(Array.isArray(data) ? data : []))
      .catch(() => setImportantOps([]));
  }, [tripId]);

  // Wave 32 · Phase 2 — Featured Reminders from Travel Prep pages,
  // fed into the LiveDayBanner so they appear in the Up Next carousel.
  const [travelPrepReminders, setTravelPrepReminders] = React.useState([]);
  React.useEffect(() => {
    if (!tripId) return;
    api.get(`/client-portal/experiences/${tripId}/travel-prep-reminders`)
      .then(({ data }) => setTravelPrepReminders(data?.reminders || []))
      .catch(() => setTravelPrepReminders([]));
  }, [tripId]);

  // Wave 11o — Saved items ("NYC folder"). Fetch on mount + provide
  // toggle handler with optimistic UI for snappy heart taps.
  const [savedFolders, setSavedFolders] = React.useState({ folders: [], items: [] });
  const refreshSaved = React.useCallback(async () => {
    if (!tripId) return;
    try {
      const { data } = await api.get(`/client-portal/trips/${tripId}/saved-items`);
      setSavedFolders(data || { folders: [], items: [] });
    } catch { /* silent */ }
  }, [tripId]);
  React.useEffect(() => { refreshSaved(); }, [refreshSaved]);
  const savedSet = React.useMemo(
    () => new Set((savedFolders.items || []).map((i) => i.item_id)),
    [savedFolders],
  );
  const handleToggleSave = React.useCallback(async (item, dayNumber, blockKey, toSave) => {
    // Optimistic UI
    setSavedFolders((cur) => {
      const items = cur.items || [];
      if (toSave) {
        return { ...cur, items: [...items, { item_id: item.id, day_number: dayNumber, block: blockKey, snapshot: { name: item.name, image_url: item.image_url } }] };
      }
      return { ...cur, items: items.filter((i) => i.item_id !== item.id) };
    });
    try {
      if (toSave) {
        await api.post(`/client-portal/trips/${tripId}/saved-items`, {
          day_number: dayNumber, block: blockKey, item_id: item.id,
        });
      } else {
        await api.delete(`/client-portal/trips/${tripId}/saved-items/${item.id}`);
      }
      refreshSaved();
    } catch {
      refreshSaved();
    }
  }, [tripId, refreshSaved]);

  React.useEffect(() => {
    let alive = true;
    api
      .get(`/client-portal/trips/${tripId}/itinerary`)
      .then(({ data }) => alive && setDays(Array.isArray(data) ? data : []))
      .catch(() => alive && setError("Couldn't load your itinerary."));
    return () => { alive = false; };
  }, [tripId]);

  // Wave 11u · Batch D — Listen for the global pull-to-refresh event
  // dispatched by the shell. Re-fetches the itinerary on demand.
  // Wave 12.e — ALSO listen for `vt:sync-changed:itinerary` so advisor
  // edits in the slot editor appear in the member's open tab within
  // the live-sync 60s heartbeat (or sooner on tab focus). Without this
  // listener the page would only refresh on a hard reload — which is
  // exactly the "changed Joya time but client still sees old time"
  // bug the owner reported.
  React.useEffect(() => {
    const refetch = () => {
      api.get(`/client-portal/trips/${tripId}/itinerary`)
        .then(({ data }) => setDays(Array.isArray(data) ? data : []))
        .catch(() => { /* keep existing data on error */ });
      refreshSaved();
    };
    window.addEventListener("vt:pull-refresh", refetch);
    window.addEventListener("vt:sync-changed:itinerary", refetch);
    window.addEventListener("vt:sync-changed:trip", refetch);
    // Catch-up on tab focus so a long-idle member sees the latest
    // immediately when they return.
    const onVis = () => {
      if (document.visibilityState === "visible") refetch();
    };
    document.addEventListener("visibilitychange", onVis);
    return () => {
      window.removeEventListener("vt:pull-refresh", refetch);
      window.removeEventListener("vt:sync-changed:itinerary", refetch);
      window.removeEventListener("vt:sync-changed:trip", refetch);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [tripId, refreshSaved]);

  const allHidden = React.useMemo(() => {
    if (!days || days.length === 0) return false;
    return days.every((d) => d.is_next_unlock || !d.visibility || d.visibility === "hidden");
  }, [days]);

  // Wave 11x — Backend returns ONE synthetic `is_next_unlock` entry for
  // the soonest gated day. Split it out so it doesn't collide with the
  // real-day rendering paths (pager, single-day route lookup, etc).
  const realDays = React.useMemo(
    () => (days ? days.filter((d) => !d.is_next_unlock) : days),
    [days],
  );
  const nextUnlockHint = React.useMemo(
    () => (days ? days.find((d) => d.is_next_unlock) || null : null),
    [days],
  );

  // Wave 26.27 · Phase 3e — Deep-link arrival pulse. Reads
  // window.location.hash (#day-3, #activity-morning-0, etc.) and
  // gently scrolls + glows the matching element once mounted.
  useArrivalFocus();

  // Wave 11u · Per-day view: find the requested day, gracefully redirect
  // to index if the day doesn't exist yet (or is hidden/unavailable).
  const focusedDay = React.useMemo(() => {
    if (!isDayView || !realDays) return null;
    return realDays.find((d) => d.day_number === requestedDayNumber) || null;
  }, [isDayView, realDays, requestedDayNumber]);

  // ─────────────────────────────────────────────────────────────
  // Render: SINGLE-DAY VIEW (Wave 11u)
  // ─────────────────────────────────────────────────────────────
  if (isDayView) {
    return (
      <motion.div
        initial="initial"
        animate="animate"
        variants={stagger}
        className="space-y-8 pb-12"
        data-testid={`client-itinerary-day-${requestedDayNumber}`}
      >
        {!insideExperienceShell && (
          <motion.div variants={fadeInUp}>
            <Link
              to={backHref}
              className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--foreground-muted))] hover:text-[hsl(var(--foreground))] transition-colors"
              data-testid="back-to-trip"
            >
              <ChevronLeft className="size-3" strokeWidth={1.5} />
              Experience home
            </Link>
          </motion.div>
        )}

        <motion.div variants={fadeInUp}>
          <Link
            to={indexHref}
            className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--foreground-muted))] hover:text-[hsl(var(--accent-gold))] transition-colors"
            data-testid="back-to-day-index"
          >
            <CalendarDays className="size-3" strokeWidth={1.5} />
            All days
          </Link>
        </motion.div>

        {error && (
          <Empty title={error} icon={<CalendarDays className="size-6" strokeWidth={1.25} />} />
        )}
        {!days && !error && (
          <Skeleton className="aspect-[5/6] sm:aspect-[21/9]" />
        )}
        {days && !focusedDay && (
          <Empty
            title={nextUnlockHint ? `Day ${nextUnlockHint.day_number} will unlock soon.` : "This chapter is still being composed."}
            hint={
              nextUnlockHint
                ? `It will quietly arrive at ${nextUnlockHint.unlock_label || "10 PM the night before"}.`
                : "Your advisor is shaping the days. Each opens at 10 PM the night before."
            }
            icon={<CalendarDays className="size-6" strokeWidth={1.25} />}
            action={
              <PortalButton as={Link} to={indexHref} variant="ghost" size="sm">
                All days
              </PortalButton>
            }
          />
        )}
        {focusedDay && (
          <DayChapter
            key={focusedDay.id || focusedDay.day_number}
            day={focusedDay}
            index={0}
            veiled={veil.veiled}
            tripId={tripId}
            savedSet={savedSet}
            onToggleSave={handleToggleSave}
            destinationTz={trip?.destination_timezone || trip?.timezone || "America/New_York"}
          />
        )}

        {days && days.length > 1 && focusedDay && (
          <DayPager
            days={realDays}
            currentDayNumber={focusedDay.day_number}
            tripId={tripId}
            isPreview={isPreview}
            base={base}
          />
        )}
      </motion.div>
    );
  }

  // ─────────────────────────────────────────────────────────────
  // Render: DAY INDEX (Wave 11u · default at /itinerary)
  // ─────────────────────────────────────────────────────────────
  return (
    <motion.div
      initial="initial"
      animate="animate"
      variants={stagger}
      className="space-y-10 sm:space-y-14 pb-12"
      data-testid="client-itinerary-root"
    >
      {/* Header — only outside the experience shell (legacy routes) */}
      {!insideExperienceShell && (
        <motion.div variants={fadeInUp}>
          <Link
            to={backHref}
            className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--foreground-muted))] hover:text-[hsl(var(--foreground))] transition-colors mb-6"
            data-testid="back-to-trip"
          >
            <ChevronLeft className="size-3" strokeWidth={1.5} />
            Experience home
          </Link>
        </motion.div>
      )}

      {/* Intro band — quiet, editorial */}
      <motion.div variants={fadeInUp} className="max-w-2xl space-y-3">
        <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-[hsl(var(--foreground-muted))]">
          The journey · day by day
        </p>
        <Heading level="display" className="!text-[32px] sm:!text-[42px] lg:!text-[52px]">
          Day by day.
        </Heading>
        <p className="font-accent italic text-[16px] sm:text-[18px] text-[hsl(var(--foreground-secondary))] leading-relaxed">
          {allHidden
            ? "Your advisor is shaping each day. They'll arrive here as they're ready."
            : "Each day, its own chapter. Tap any card to open it."}
        </p>
      </motion.div>

      {/* Wave 26.24 · Phase 3b — Trip-level destination ribbon (silent when veiled). */}
      {trip && !veil.veiled && (
        <motion.div variants={fadeInUp}>
          <TripDestinationRibbon trip={trip} />
        </motion.div>
      )}

      {/* Wave 12.i — Live Day Banner: auto-rotating carousel surfacing
          the day's priority note, important item notes, and live
          "Now / Up next" computed in the destination's local time. */}
      {realDays && realDays.length > 0 && (
        <motion.div variants={fadeInUp}>
          <LiveDayBanner days={realDays} timezone={trip?.destination_timezone || trip?.timezone || "America/New_York"} bookings={importantOps} travelPrepReminders={travelPrepReminders} />
        </motion.div>
      )}

      {/* Wave 26.25 · Phase 3c — Investment band (silent when nothing shared). */}
      {trip && (
        <motion.div variants={fadeInUp}>
          <InvestmentBand tripId={tripId} />
        </motion.div>
      )}

      <motion.div variants={fadeInUp}>
        <EditorialRule label={realDays ? `${realDays.length} day${realDays.length === 1 ? "" : "s"}` : "loading"} />
      </motion.div>

      {/* Wave 11o — Saved folder (your hearts) */}
      {savedFolders.folders && savedFolders.folders.length > 0 && (
        <motion.div variants={fadeInUp}>
          <SavedFolderCard
            folders={savedFolders.folders}
            onUnsave={(item) => handleToggleSave({ id: item.item_id, name: item.snapshot?.name }, item.day_number, item.block, false)}
            tripId={tripId}
          />
        </motion.div>
      )}

      {/* Day INDEX — each day is its own card with hero. Tap → opens
          the full single-day page at /itinerary/day/:N. */}
      <div className="grid sm:grid-cols-2 gap-4 sm:gap-5">
        {error ? (
          <Empty title={error} icon={<CalendarDays className="size-6" strokeWidth={1.25} />} />
        ) : !days ? (
          <>
            <Skeleton className="aspect-[5/4] sm:aspect-[16/9]" />
            <Skeleton className="aspect-[5/4] sm:aspect-[16/9]" />
            <Skeleton className="aspect-[5/4] sm:aspect-[16/9] hidden sm:block" />
            <Skeleton className="aspect-[5/4] sm:aspect-[16/9] hidden sm:block" />
          </>
        ) : days.length === 0 ? (
          <div className="sm:col-span-2">
            <Empty
              title="Your itinerary is being arranged"
              hint="Your advisor will share each day here as it's ready."
              icon={<CalendarDays className="size-6" strokeWidth={1.25} />}
              action={
                <PortalButton as={Link} to={backHref} variant="ghost" size="sm">
                  Back
                </PortalButton>
              }
            />
          </div>
        ) : (
          <>
            {realDays.map((day, i) => (
              <DayIndexCard
                key={day.id || day.day_number}
                day={day}
                index={i}
                tripId={tripId}
                isPreview={isPreview}
                base={base}
              />
            ))}
            {nextUnlockHint && (
              <NextUnlockCard key={`next-unlock-${nextUnlockHint.day_number}`} day={nextUnlockHint} />
            )}
          </>
        )}
      </div>

      {/* Wave 26.26 · Phase 3d — Approval workflow CTAs + comment thread. */}
      {trip && realDays && realDays.length > 0 && (
        <motion.div variants={fadeInUp}>
          <ApprovalBlock tripId={tripId} />
        </motion.div>
      )}
    </motion.div>
  );
}
